import { useEffect, useState } from 'react';
import { useParams, useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPlayerById } from '../../../features/playerProfile/playerProfileSlice';
import { followPlayer, unfollowPlayer, getCoachDashboard } from '../../../features/coach/coachProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import { VideoSectionView } from './components/VideoSectionView';
import { StatisticsSectionView } from './components/StatisticsSectionView';
import { CoachRecommendationView } from './components/CoachRecommendationView';
import { CoachSidebar } from '../../../components/coach/dashboard/CoachSidebar';
import { TeamLogoBox } from '../../../components/common/TeamLogoBox';
import { ContactDetails } from '../../dashboard/profile/PlayerProfileHeader';
import { CARD, CardTitle, InfoGrid, BulletList } from '../../dashboard/profile/profileCard';
import verifiedIcon from '../../../assets/icons/verified.png';

// Date of birth with age, e.g. "04/12/2005 (21)"
const formatDOB = (dob) => {
  if (!dob || dob === '-') return '-';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '-';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;
  return `${mm}/${dd}/${d.getFullYear()} (${age})`;
};

// Headshot in the same cyan-bordered square as the homepage preview
const PlayerPhoto = ({ name, src }) => {
  const [failed, setFailed] = useState(false);
  const frame = 'w-32 h-32 sm:w-40 sm:h-40 flex-shrink-0 rounded-2xl border-2 border-cyan-400/60 overflow-hidden';

  if (src && !failed) {
    return <img src={src} alt={name} onError={() => setFailed(true)} className={`${frame} object-cover bg-white/10`} />;
  }

  return (
    <div className={`${frame} bg-white/10 flex items-end justify-center`} role="img" aria-label={`${name} photo`}>
      <svg className="w-4/5 h-4/5 text-white/40" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6z" />
      </svg>
    </div>
  );
};

export const PlayerProfileView = () => {
  const { playerId } = useParams();
  const { activeView } = useOutletContext();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { viewedPlayer, viewedPlayerLoading, viewedPlayerError } = useSelector((state) => state.playerProfile);
  const { followedPlayers } = useSelector((state) => state.coachProfile);

  // Check if player data passed via navigation state
  const [playerFromState] = useState(location.state?.playerData || null);
  const [followLoading, setFollowLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);

  // Fetch player data only if not available in state
  useEffect(() => {
    if (!playerFromState && playerId) {
      dispatch(getPlayerById(playerId));
    }
  }, [dispatch, playerId, playerFromState]);

  // Fetch followed players on mount
  useEffect(() => {
    dispatch(getCoachDashboard({ page: 1, limit: 100 }));
  }, [dispatch]);

  // Use data from state if available, otherwise use fetched data
  const currentPlayer = playerFromState || viewedPlayer;

  // Check if current player is in followed players list
  useEffect(() => {
    const currentPlayerId = currentPlayer?._id || playerId;
    if (currentPlayerId && followedPlayers && followedPlayers.length > 0) {
      const isPlayerFollowed = followedPlayers.some(
        (player) => player._id === currentPlayerId || player.id === currentPlayerId
      );
      setIsFollowing(isPlayerFollowed);
    } else {
      setIsFollowing(false);
    }
  }, [currentPlayer, playerId, followedPlayers]);

  const handleFollowToggle = async () => {
    const currentPlayerId = currentPlayer?._id || playerId;
    if (!currentPlayerId || followLoading) return;

    setFollowLoading(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowPlayer(currentPlayerId)).unwrap();
        setIsFollowing(false);
      } else {
        await dispatch(followPlayer(currentPlayerId)).unwrap();
        setIsFollowing(true);
      }
      // Refresh the followed players list to keep it in sync
      dispatch(getCoachDashboard({ page: 1, limit: 100 }));
    } catch (error) {
      console.error('Failed to toggle player follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };
  // Handle stats from roster API (stats.battingStats) vs profile API (battingStats)
  // Also handle both array and object formats
  const battingStats = currentPlayer?.stats?.battingStats
    ? [currentPlayer.stats.battingStats]
    : (currentPlayer?.battingStats
      ? (Array.isArray(currentPlayer.battingStats) ? currentPlayer.battingStats : [currentPlayer.battingStats])
      : []);

  const pitchingStats = currentPlayer?.stats?.pitchingStats
    ? [currentPlayer.stats.pitchingStats]
    : (currentPlayer?.pitchingStats
      ? (Array.isArray(currentPlayer.pitchingStats) ? currentPlayer.pitchingStats : [currentPlayer.pitchingStats])
      : []);

  const fieldingStats = currentPlayer?.stats?.fieldingStats
    ? [currentPlayer.stats.fieldingStats]
    : (currentPlayer?.fieldingStats
      ? (Array.isArray(currentPlayer.fieldingStats) ? currentPlayer.fieldingStats : [currentPlayer.fieldingStats])
      : []);

  // Map player data to expected format
  const playerData = currentPlayer ? {
    id: currentPlayer._id,
    name: `${currentPlayer.firstName || ''} ${currentPlayer.lastName || ''}`.trim() || currentPlayer.name || '-',
    photo: currentPlayer.profileImage || null,
    dateOfBirth: currentPlayer.dob || currentPlayer.dateOfBirth || '-',
    class: normalizePlayerClass(currentPlayer.playerClass || currentPlayer.class),
    bt: currentPlayer.batsThrows || currentPlayer.batThrow || '-',
    position: currentPlayer.primaryPosition || currentPlayer.position || '-',
    hometown: currentPlayer.hometown || '-',
    commitment: currentPlayer.commitmentStatus || '-',
    highSchool: currentPlayer.highSchool || '-',
    college: currentPlayer.previousSchool || '-',
    height: currentPlayer.height || '-',
    weight: currentPlayer.weight ? (typeof currentPlayer.weight === 'number' ? `${currentPlayer.weight} LBS` : currentPlayer.weight) : '-',
    eligibilityYears: [],
    bio: currentPlayer.playerBio || currentPlayer.description || currentPlayer.bio,
    gpa: currentPlayer.academic_info_gpa || '-',
    sat: currentPlayer.academic_info_sat || '-',
    act: currentPlayer.academic_info_act || '-',
    classRank: '-',
    jerseyNumber: currentPlayer.jerseyNumber || null,
    committedTo: currentPlayer.committedTo || '-',
    WHIP: currentPlayer.WHIP || '-',
    // Include team data
    team: currentPlayer.team || null,
    // Profile status and contact info
    registrationStatus: currentPlayer.registrationStatus || null,
    phoneNumber: currentPlayer.phoneNumber || null,
    email: currentPlayer.email || null,
    // Social media links
    instaURL: currentPlayer.instaURL || null,
    xURL: currentPlayer.xURL || null,
    // NCAA ID
    ncaaId: currentPlayer.ncaaId || null,
  } : null;
  const schoolName = playerData?.team && typeof playerData.team === 'object' ? playerData.team.name : '';
  const subtitle = [playerData?.position !== '-' && playerData?.position, schoolName].filter(Boolean).join(' · ');
  const isClaimed = playerData?.registrationStatus === 'approved';
  const previousTeams = currentPlayer?.previousTeams
    ? String(currentPlayer.previousTeams).split(',').map((t) => t.trim()).filter(Boolean)
    : [];
  const transcript = currentPlayer?.acedemicInfo || currentPlayer?.academicInfo;
  const transcriptUrl = transcript?.url || transcript?.documentUrl || (typeof transcript === 'string' ? transcript : null);
  const recommendation = currentPlayer?.coachRecommendation;

  const openMessages = () => {
    const rolePrefix = location.pathname.split('/')[1] || 'coach';
    navigate(`/${rolePrefix}/messages`, {
      state: {
        newConversationPlayer: {
          _id: currentPlayer?._id,
          firstName: currentPlayer?.firstName || '',
          lastName: currentPlayer?.lastName || '',
          profileImage: currentPlayer?.profileImage || null,
        },
      },
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Left Sidebar */}
      <div className="lg:col-span-1">
        <CoachSidebar activeView={activeView} />
      </div>

      {/* Right Content Area */}
      <main className="lg:col-span-3 min-w-0 text-left">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 inline-flex items-center gap-2 text-jp-muted hover:text-jp-ink text-sm font-medium transition-colors"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
          <span>Back</span>
        </button>

        {/* Loading State - only show when fetching from API */}
        {!playerFromState && viewedPlayerLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="w-12 h-12 border-4 border-jp-accent border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error State */}
        {!playerFromState && viewedPlayerError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/40 rounded-xl text-red-600 dark:text-red-400 text-sm">
            {viewedPlayerError}
          </div>
        )}

        {/* Profile Content, laid out like the homepage player preview */}
        {playerData && (
          <div className="space-y-6">
            <section className="bg-jp-band rounded-3xl px-5 sm:px-8 py-6 sm:py-8">
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
                <div className="flex flex-col sm:flex-row sm:items-center gap-5 min-w-0">
                  <div className="relative flex-shrink-0 self-start">
                    <PlayerPhoto name={playerData.name} src={playerData.photo} />
                    {isClaimed && (
                      <img
                        src={verifiedIcon}
                        alt="Verified"
                        title="Verified Profile"
                        className="absolute top-0 right-0 translate-x-1/3 -translate-y-1/3 w-10 h-10"
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-2">Player Profile</p>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-anton leading-[1.1] tracking-tight text-white uppercase break-words">
                      {playerData.name}
                    </h1>
                    {/* Position and school, with the jersey number to the right of the school */}
                    {(subtitle || playerData.jerseyNumber) && (
                      <p className="text-slate-300 text-sm sm:text-base mt-2">
                        {subtitle}
                        {playerData.jerseyNumber && (
                          <span className="ml-2 text-white font-bold">#{playerData.jerseyNumber}</span>
                        )}
                      </p>
                    )}
                    {/* Phone and email only once the player has claimed the profile */}
                    <ContactDetails profile={currentPlayer} hidePrivate={!isClaimed} />
                  </div>
                </div>

                <div className="flex flex-row xl:flex-col items-center xl:items-end gap-4 flex-shrink-0">
                  {playerData.team && typeof playerData.team === 'object' && (
                    <TeamLogoBox
                      name={schoolName}
                      logo={playerData.team.logo}
                      onClick={playerData.team._id ? () => navigate(`/coach/team/${playerData.team._id}`) : undefined}
                    />
                  )}
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      onClick={openMessages}
                      className="flex items-center gap-2 px-5 py-2.5 bg-white/10 border border-white/25 hover:bg-white/20 text-white rounded-lg font-semibold transition-colors"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>Message</span>
                    </button>
                    <button
                      onClick={handleFollowToggle}
                      disabled={followLoading}
                      className={`${
                        isFollowing
                          ? 'bg-white/10 border border-white/25 hover:bg-white/20'
                          : 'bg-cyan-500 hover:bg-cyan-600'
                      } text-white px-6 py-2.5 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2`}
                    >
                      {followLoading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>{isFollowing ? 'Unfollowing...' : 'Following...'}</span>
                        </>
                      ) : (
                        <>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            {isFollowing ? (
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            ) : (
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                            )}
                          </svg>
                          <span>{isFollowing ? 'Unfollow Player' : 'Follow Player'}</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {/* Same cards, in the same order, as the homepage preview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <section className={`${CARD} lg:col-span-2 min-w-0`}>
                <CardTitle title="Player Information" />
                <InfoGrid
                  items={[
                    { label: 'Position', value: playerData.position },
                    { label: 'College', value: schoolName || (playerData.college !== '-' ? playerData.college : '') },
                    { label: 'Class', value: playerData.class },
                    { label: 'B/T', value: playerData.bt },
                    { label: 'Height', value: playerData.height },
                    { label: 'Weight', value: playerData.weight },
                    { label: 'Date of Birth', value: formatDOB(playerData.dateOfBirth) },
                    { label: 'Hometown', value: playerData.hometown },
                    { label: 'High School', value: playerData.highSchool },
                    {
                      label: 'Commitment',
                      value: playerData.commitment === 'committed' && playerData.committedTo !== '-' ? playerData.committedTo : 'Uncommitted',
                    },
                    { label: 'NCAA ID Number', value: playerData.ncaaId },
                  ]}
                />
              </section>

              <section className={`${CARD} lg:col-span-2 min-w-0`}>
                <StatisticsSectionView
                  battingStats={battingStats}
                  pitchingStats={pitchingStats}
                  fieldingStats={fieldingStats}
                  conferenceData={currentPlayer?.conferenceData || null}
                  summerData={currentPlayer?.summerData || null}
                  position={playerData.position}
                  teamName={schoolName || (typeof playerData.team === 'string' ? playerData.team : null)}
                  teamLeague={playerData.team && typeof playerData.team === 'object' ? playerData.team.league : null}
                />
              </section>

              <section className={`${CARD} lg:col-span-2 min-w-0`}>
                <VideoSectionView
                  videos={currentPlayer?.videos || []}
                  profile={currentPlayer}
                  playerId={currentPlayer?._id || playerId}
                />
              </section>

              <section className={`${CARD} lg:col-span-2`}>
                <CardTitle title="Bio" />
                <p className="text-jp-muted text-sm leading-relaxed whitespace-pre-line">
                  {playerData.bio || 'No bio yet.'}
                </p>
              </section>

              <section className={CARD}>
                <CardTitle title="Previous Teams" />
                <BulletList items={previousTeams} empty="No previous teams listed." />
              </section>

              <section className={CARD}>
                <CardTitle title="Awards & Achievements" />
                <BulletList items={currentPlayer?.awardsAchievements || []} empty="No awards listed yet." />
              </section>

              <section className={`${CARD} lg:col-span-2`}>
                <CardTitle title="Academic Information">
                  {transcriptUrl && (
                    <a
                      href={transcriptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-semibold text-jp-accent hover:underline underline-offset-4"
                    >
                      View transcript
                    </a>
                  )}
                </CardTitle>
                <InfoGrid
                  items={[
                    { label: 'GPA', value: playerData.gpa },
                    { label: 'SAT Score', value: playerData.sat },
                    { label: 'ACT Score', value: playerData.act },
                  ]}
                />
              </section>

              {/* Older profiles may still have an uploaded recommendation letter */}
              {recommendation?.url && (
                <section className={`${CARD} lg:col-span-2`}>
                  <CoachRecommendationView recommendation={{ ...recommendation, position: playerData.position }} />
                </section>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PlayerProfileView;
